/**
 * Verification mode registry + thin
 * dispatcher.
 *
 * The four verification modes promised by the catalog:
 *
 *  - `parse-only` — default; nothing leaves the device.
 *  - `dns-enabled` — DoH lookups for SPF / DKIM / DMARC / MX / PTR.
 *  - `crypto-enabled` — `crypto.subtle` verification of DKIM body
 *  hashes + DKIM RSA signatures (when a public
 *  key is available), plus surface stubs for
 *  S/MIME and OpenPGP (verified via existing
 *  in-tree code paths in `src/auth/dkim-verify.ts`
 *  and `src/derivations/smime-cert.ts`).
 *  - `sandboxed` — opt-in worker that inspects risky surfaces
 *  (HTML / archive listings / document metadata)
 *  with bounded resources.
 *
 * Every mode is opt-in: the default stays `parse-only`. The dispatcher
 * never fires a verifier itself — callers reach into a `runVerification`
 * helper (or lazy-load `./dns-worker`, `./crypto`, `./sandbox-worker`).
 * That keeps the bundle small (each verifier is a separate chunk) and
 * means the privacy gate (`scripts/check-network-calls.mjs`) can keep
 * blanket-allowlisting a single, narrow surface.
 *
 * Design notes:
 *
 * - The registry shape is data-only — `VERIFICATION_MODE_DESCRIPTORS`
 *  carries human-readable consent copy keys for the settings UI and a
 *  `network` boolean so privacy-conscious UI surfaces (badge tints in
 *  the Settings sidebar, the privacy implications block on the
 *  Verification section) can render correctly without hard-coding the
 *  list of modes.
 * - The dispatcher does not maintain state. Verifier results are
 *  cached at the call site (e.g. `DkimTxtLookupSurface`'s rate-limit
 *  cache for DKIM TXT lookups).
 */

import type { VerificationMode } from "../parser-core/provenance";

export type { VerificationMode } from "../parser-core/provenance";

/**
 * What a single mode allows the verifier to do. The descriptor flags
 * surface in the Settings privacy block + in axe-friendly aria copy on
 * the chip.
 */
export interface VerificationModeDescriptor {
  /** Stable identifier used as the localStorage value. */
  id: VerificationMode;
  /** True iff the mode triggers any off-device network call. */
  network: boolean;
  /** True iff the mode runs `crypto.subtle` operations. */
  crypto: boolean;
  /** True iff the mode spins a sandboxed Web Worker. */
  sandboxed: boolean;
  /** Localised label key — see `VerificationModeCopy.labelKey`. */
  labelKey: "parseOnly" | "dnsEnabled" | "cryptoEnabled" | "sandboxed";
  /**
   * Whether the verifier path is fully wired up today. Modes with
   * `available: false` still appear in the descriptor list (so docs +
   * the type-level union don't drift) but UI surfaces gate the
   * selectable affordance on this flag — the radio / chip option
   * disables itself and a "Coming soon" footnote replaces the body.
   */
  available: boolean;
}

/** Authoritative list of the four modes in display order. */
export const VERIFICATION_MODE_DESCRIPTORS: readonly VerificationModeDescriptor[] = [
  {
    id: "parse-only",
    network: false,
    crypto: false,
    sandboxed: false,
    labelKey: "parseOnly",
    available: true,
  },
  {
    id: "dns-enabled",
    network: true,
    crypto: false,
    sandboxed: false,
    labelKey: "dnsEnabled",
    available: true,
  },
  {
    id: "crypto-enabled",
    network: false,
    crypto: true,
    sandboxed: false,
    labelKey: "cryptoEnabled",
    available: true,
  },
  {
    id: "sandboxed",
    network: false,
    crypto: false,
    sandboxed: true,
    labelKey: "sandboxed",
    // The sandboxed worker path is now wired end-to-end. `useEmail`
    // lazy-loads `runSandboxPipeline` when the user picks `verificationMode
    // === "sandboxed"`, dispatches the HTML / archive-list / doc-meta
    // surfaces to the Web Worker (`src/verification/sandbox-worker.ts`) via
    // `runSandboxOffMain` (`src/verification/sandbox-runner.ts`, Wave 31.5),
    // and attaches the result to `analysis.sandbox` for downstream consumers.
    // previously history (Wave 2 UI overhaul / audit F-006): the descriptor
    // briefly advertised `available: true`, then was demoted to `false` when
    // the audit revealed no call site spawned the worker. Wave 31.5 shipped
    // the worker module + the off-main runner. Wave 40 ties the analyser
    // pipeline through them and flips this flag back to `true` so the
    // Settings radio + header chip stop showing a "coming soon" footnote.
    available: true,
  },
] as const;

/** Look up a descriptor by mode; never returns `undefined` for a valid id. */
export function describeMode(mode: VerificationMode): VerificationModeDescriptor {
  const d = VERIFICATION_MODE_DESCRIPTORS.find((x) => x.id === mode);
  /* istanbul ignore next — exhaustive over the literal union */
  if (!d) throw new Error(`Unknown verification mode: ${String(mode)}`);
  return d;
}

/**
 * True iff the supplied mode is at least as permissive as `floor`. Used
 * by call sites that gate optional behaviour (e.g. the auto-run on
 * `DkimTxtLookupSurface`) on "dns-enabled or higher" without enumerating
 * every mode.
 *
 * Order: parse-only (least) < dns-enabled < crypto-enabled < sandboxed
 * (most). The order matches the resource cost of each mode (none, DoH
 * fetch, crypto.subtle digest + verify, worker boot). It is NOT a
 * superset ordering — e.g. sandboxed does not automatically imply DNS.
 * Use `describeMode(mode).network` if you need the boolean flags.
 */
export function modeAtLeast(mode: VerificationMode, floor: VerificationMode): boolean {
  const order: VerificationMode[] = [
    "parse-only",
    "dns-enabled",
    "crypto-enabled",
    "sandboxed",
  ];
  return order.indexOf(mode) >= order.indexOf(floor);
}

/**
 * True iff the mode allows DoH lookups today. Reads as
 * `describeMode(mode).network`; kept as a named helper so the privacy
 * gate test can assert the API surface without poking at the
 * descriptor table.
 */
export function modeAllowsNetwork(mode: VerificationMode): boolean {
  return describeMode(mode).network;
}

/** True iff the mode allows `crypto.subtle` calls. */
export function modeAllowsCrypto(mode: VerificationMode): boolean {
  return describeMode(mode).crypto;
}

/** True iff the mode allows sandboxed analysis workers. */
export function modeAllowsSandbox(mode: VerificationMode): boolean {
  return describeMode(mode).sandboxed;
}
